import { Tab } from 'tab-election'
import { StateMachineService } from './StateMachineService'
import { log } from '../logger'
import {
  Job,
  StateMachineContext,
  StateMachineState,
} from './types'
import type { TabEventMap } from 'tab-election'

export interface StateMachineHubOptions<
  State extends StateMachineState,
  Context extends StateMachineContext
> {
  namespace?: string
  initialState: Omit<State, 'processing'>
  init?: () => Promise<void>
  resolveContext?: (state: State, job: Job) => Promise<Context>
}

export class StateMachineHub<
  State extends StateMachineState,
  Context extends StateMachineContext
> implements EventTarget
{
  id = crypto.randomUUID()
  private hub: Tab
  readonly service: StateMachineService<State, Context>
  readonly options: StateMachineHubOptions<State, Context>

  constructor(options: StateMachineHubOptions<State, Context>) {
    this.options = options
    this.service = new StateMachineService({
      resolveContext: options.resolveContext,
    })
    this.hub = new Tab(options.namespace || 'web-queue')
    this.hub.addEventListener('state', this.stateChanged)
  }

  addEventListener<K extends keyof TabEventMap>(
    type: K,
    listener: (
      this: BroadcastChannel,
      ev: TabEventMap[K]
    ) => void,
    options?: boolean | AddEventListenerOptions
  ): void {
    return this.hub.addEventListener(type, listener, options)
  }

  dispatchEvent(event: Event): boolean {
    return this.hub.dispatchEvent(event)
  }

  removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions
  ): void {
    return this.hub.removeEventListener(type, listener, options)
  }

  waitForLeadership() {
    this.hub
      .waitForLeadership(async () => {
        log.info(
          'hub: New leader',
          this.id,
          'state:',
          this.hub.getState()
        )
        if (this.isStateEmpty()) {
          this.setState({
            ...this.options.initialState,
            processing: false,
          } as State)
        }
        await this.options.init?.()
        await this.service.init(this.hub)
        this.hub.dispatchEvent(new Event('leader'))
        setTimeout(() => {
          this.setState({ processing: true } as Partial<State>)
          this.service.startProcessing()
        }, 500)
        return {
          cancelJob: this.service.cancelJob.bind(this.service),
          enqueue: this.service.enqueue.bind(this.service),
          updateState: (state: Partial<State>) => {
            this.setState(state)
          },
          startProcessing: () => {
            console.warn('hub: noop start processing called')
            // this.setState({ processing: true } as Partial<State>)
            // this.service.startProcessing()
          },
          stopProcessing: () => {
            console.warn('hub: noop stop processing called')
          },
        }
      })
      .then(() => {
        return this.destroy()
      })
  }

  isStateEmpty(): boolean {
    return Object.keys(this.hub.getState()).length === 0
  }

  setState(state: Partial<State>) {
    if (!this.hub.isLeader) return
    const currentState = this.hub.getState()
    this.hub.setState({
      ...currentState,
      ...state,
    })
  }

  private stateChanged = (_e: MessageEvent<State>) => {
    // console.debug('state changed', e.data)
  }

  async destroy(): Promise<void> {
    console.log('StateMachineHub destroying', this.id)
    await this.service.destroy()
    // Hub cleanup would be handled by tab-election
  }
}
